import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MapPin, Plus, Sparkles, ShieldAlert, Store, CalendarDays, Wrench, Utensils, Baby, Heart, ArrowRight, Check } from "lucide-react";
import NeighborhoodSelect from "@/components/NeighborhoodSelect";

const INTERESTS = [
  { id: "safety",       label: "Safety alerts",       icon: ShieldAlert },
  { id: "marketplace",  label: "Marketplace deals",   icon: Store },
  { id: "events",       label: "Events & meetups",    icon: CalendarDays },
  { id: "recommend",    label: "Recommendations",     icon: Wrench },
  { id: "food",         label: "Food & local eats",   icon: Utensils },
  { id: "family",       label: "Kids & family",       icon: Baby },
  { id: "utility",      label: "Utility & roads",     icon: Sparkles },
  { id: "wellness",     label: "Wellness & fitness",  icon: Heart },
];

/**
 * Two-step onboarding modal for brand-new neighbours.
 *   Step 1: pick a community (popular chips or free-text)
 *   Step 2: optional — pick a few interests to personalise the feed
 * Non-dismissable during step 1. Interests can be skipped.
 */
export default function CommunityOnboardingModal() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("hood"); // hood | interests
  const [popular, setPopular] = useState([]);
  const [selectedHood, setSelectedHood] = useState(null);
  const [custom, setCustom] = useState("");
  const [interests, setInterests] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/neighborhoods");
        setPopular(data.popular || []);
        const joined = data.neighborhoods || [];
        if (joined.length === 0) {
          setStep("hood");
          setOpen(true);
          return;
        }
        // If they're already in a community but haven't chosen interests, show the second step only
        const { data: prefs } = await api.get("/preferences");
        if (!prefs.interests || prefs.interests.length === 0) {
          setStep("interests");
          setOpen(true);
        }
      } catch { /* silent */ }
    })();
  }, [user]);

  const submitHood = async (name) => {
    const clean = (name || custom || selectedHood || "").trim();
    if (!clean || busy) return;
    setBusy(true);
    try {
      await api.post("/neighborhoods/join", { name: clean });
      await refresh();
      toast.success(`Karibu ${clean}!`);
      setStep("interests");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const toggleInterest = (id) => {
    setInterests((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const submitInterests = async (skip = false) => {
    setBusy(true);
    try {
      await api.patch("/preferences", { interests: skip ? [] : interests });
      if (!skip) toast.success("Perfect — your feed will feel more like home now.");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={() => { /* not dismissable during hood step */ }}>
      <DialogContent
        data-testid="community-onboarding-modal"
        className={`max-w-lg gap-0 overflow-hidden rounded-3xl border-black/5 p-0 sm:rounded-3xl ${step === "hood" ? "[&>button.absolute]:hidden" : ""}`}
        onEscapeKeyDown={(e) => step === "hood" && e.preventDefault()}
        onPointerDownOutside={(e) => step === "hood" && e.preventDefault()}
        onInteractOutside={(e) => step === "hood" && e.preventDefault()}
      >
        <div className="relative bg-[#2E473B] p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="jirani-eyebrow !text-[#F4A261]">Karibu Jirani</div>
            <div className="flex items-center gap-1.5">
              <span className={`h-1.5 w-6 rounded-full ${step === "hood" ? "bg-[#F4A261]" : "bg-white/25"}`} />
              <span className={`h-1.5 w-6 rounded-full ${step === "interests" ? "bg-[#F4A261]" : "bg-white/25"}`} />
            </div>
          </div>
          <DialogHeader className="mt-2">
            <DialogTitle
              className="flex items-center gap-2 text-2xl text-white"
              style={{ fontFamily: "Outfit", fontWeight: 700 }}
            >
              <Sparkles className="h-5 w-5 text-[#F4A261]" />
              {step === "hood"
                ? "Which neighbourhood are you in?"
                : "What are you interested in?"}
            </DialogTitle>
            <DialogDescription className="mt-2 text-white/80">
              {step === "hood"
                ? "Pick your community so we can connect you with the neighbours closest to home."
                : "Pick a few topics and your feed will start personalised on day one. You can change these later."}
            </DialogDescription>
          </DialogHeader>
        </div>

        {step === "hood" ? (
          <div className="p-6">
            <div className="text-xs uppercase tracking-widest text-[#2E473B]/70">Popular in Nairobi</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {popular.map((h) => (
                <button
                  key={h}
                  data-testid={`onboarding-pick-${h.replace(/\s+/g, "-")}`}
                  onClick={() => { setSelectedHood(h); setCustom(""); }}
                  disabled={busy}
                  className={`jirani-pill inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${
                    selectedHood === h
                      ? "border-transparent bg-[#D95D39] text-white"
                      : "border-black/10 bg-white text-[#2E473B] hover:bg-[#F9F7F4]"
                  }`}
                >
                  <MapPin className="h-3 w-3" /> {h}
                </button>
              ))}
            </div>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-[#2E473B]/70">Or pick from the list</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1">
                  <NeighborhoodSelect
                    testId="onboarding-custom-input"
                    value={custom}
                    onChange={(v) => { setCustom(v); setSelectedHood(null); }}
                    placeholder="Pick from Nairobi, Kiambu or Mombasa…"
                  />
                </div>
                <Button
                  type="button"
                  data-testid="onboarding-custom-btn"
                  onClick={() => submitHood(custom)}
                  disabled={!custom.trim() || busy}
                  variant="outline"
                  className="jirani-pill rounded-full border-black/10 bg-white px-4 text-[#2E473B]"
                >
                  <Plus className="mr-1 h-4 w-4" /> Add
                </Button>
              </div>
            </div>

            <div className="mt-8 border-t border-black/5 pt-4">
              <Button
                data-testid="onboarding-continue-btn"
                onClick={() => submitHood()}
                disabled={busy || !(selectedHood || custom.trim())}
                className="jirani-pill h-12 w-full rounded-full bg-[#D95D39] text-base font-semibold text-white hover:bg-[#c25333]"
              >
                {busy ? "Joining..." : selectedHood
                  ? `Continue to ${selectedHood}`
                  : custom.trim()
                    ? `Continue to ${custom.trim()}`
                    : "Pick a neighbourhood to continue"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="grid grid-cols-2 gap-2">
              {INTERESTS.map(({ id, label, icon: Icon }) => {
                const on = interests.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    data-testid={`interest-${id}`}
                    onClick={() => toggleInterest(id)}
                    className={`jirani-pill relative flex items-center gap-2 rounded-2xl border px-3 py-2.5 text-left text-sm transition-colors ${
                      on
                        ? "border-transparent bg-[#2E473B] text-white"
                        : "border-black/10 bg-white text-[#1A1A1A] hover:bg-[#F9F7F4]"
                    }`}
                  >
                    <div
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${
                        on ? "bg-[#D95D39] text-white" : "bg-[#F9F7F4] text-[#2E473B]"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className="flex-1 font-medium">{label}</span>
                    {on && <Check className="h-4 w-4 text-[#F4A261]" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                data-testid="onboarding-skip-interests"
                onClick={() => submitInterests(true)}
                disabled={busy}
                className="text-xs font-medium text-[#2E473B]/70 hover:text-[#2E473B]"
              >
                Skip for now
              </button>
              <Button
                data-testid="onboarding-finish-btn"
                onClick={() => submitInterests(false)}
                disabled={busy || interests.length === 0}
                className="jirani-pill h-12 flex-1 rounded-full bg-[#D95D39] text-base font-semibold text-white hover:bg-[#c25333]"
              >
                {busy ? "Saving..." : interests.length > 0
                  ? `Finish · ${interests.length} picked`
                  : "Pick at least one"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
